import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { Button } from '@/components/ui/button';
import { FormModal } from '@/components/ui/form-modal';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ConfirmDeleteDialog } from 'components/Reports/ConfirmDeleteDialog';
import {
  mapResourceSetToEditPayload,
  useResourceSets,
  useUpdateResourceSet,
} from 'components/IAP/hooks/resourceSetHooks';
import { NULL_VALUE } from 'constants/displayConstants';
import {
  IncidentPosition,
  useDetachResourceFromRoster,
  useGetPositionsForIncident,
  useGetRosterForIncident,
  useUpdateRosterResource,
} from 'hooks/incident/useRoster';
import { getDescendantIds } from './getDescendantIds';
import {
  SearchableSelect,
  type SearchableSelectOption,
} from './SearchableSelect';

type RosterResourceModalProps = {
  show: boolean;
  position: IncidentPosition;
  onClose: () => void;
};

const RosterResourceModal = ({
  show,
  position,
  onClose,
}: RosterResourceModalProps) => {
  const { data: positions = [] } = useGetPositionsForIncident();
  const { data: rosterEntries = [] } = useGetRosterForIncident();
  const { data: resourceSets = [] } = useResourceSets();

  const { mutate: updateRosterResource } = useUpdateRosterResource();
  const { mutate: detachResource, isPending: isDetaching } =
    useDetachResourceFromRoster();
  const { mutate: updateResourceSet } = useUpdateResourceSet(
    position.resourceSetId ?? ''
  );

  const resourceSet = useMemo(
    () =>
      resourceSets.find(
        (candidate) => candidate.resource_set_id === position.resourceSetId
      ),
    [resourceSets, position.resourceSetId]
  );

  const [pointOfContact, setPointOfContact] =
    useState<SearchableSelectOption | null>(null);
  const [reportsTo, setReportsTo] = useState<SearchableSelectOption | null>(
    null
  );
  const [owningOrganization, setOwningOrganization] = useState('');
  const [capabilities, setCapabilities] = useState('');
  const [quantity, setQuantity] = useState('');
  const [unit, setUnit] = useState('');
  const [notes, setNotes] = useState('');
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);

  const pointOfContactOptions = useMemo(
    () =>
      rosterEntries.map((entry) => ({
        value: entry.userId,
        label: entry.userName ?? entry.emailAddress ?? entry.userId,
      })),
    [rosterEntries]
  );

  // The queries backing these fields may still be in flight when the modal mounts, so seed the form once each source
  // arrives rather than in the initial state. The selection is read out of the options list so the closed control and
  // the open menu cannot disagree on how a person is labelled.
  useEffect(() => {
    setPointOfContact(
      pointOfContactOptions.find(
        (option) => option.value === position.pointOfContactUserId
      ) ?? null
    );
  }, [pointOfContactOptions, position.pointOfContactUserId]);

  const reportsToOptions = useMemo(() => {
    const ineligible = getDescendantIds(positions, position.id);
    ineligible.add(position.id);

    return positions
      .filter((candidate) => !ineligible.has(candidate.id))
      .map((candidate) => ({ value: candidate.id, label: candidate.name }));
  }, [positions, position.id]);

  useEffect(() => {
    setReportsTo(
      reportsToOptions.find((option) => option.value === position.parent_id) ??
        null
    );
  }, [reportsToOptions, position.parent_id]);

  useEffect(() => {
    setOwningOrganization(resourceSet?.owning_organization ?? '');
    setCapabilities(resourceSet?.description ?? '');
    setQuantity(
      resourceSet?.quantity != null ? String(resourceSet.quantity) : ''
    );
    setUnit(resourceSet?.unit ?? '');
    setNotes(resourceSet?.notes ?? '');
  }, [resourceSet]);

  const onSave = () => {
    const nextPointOfContact = pointOfContact
      ? String(pointOfContact.value)
      : undefined;
    const nextReportsTo = reportsTo ? String(reportsTo.value) : null;

    const rosterFieldsChanged =
      nextPointOfContact !== (position.pointOfContactUserId ?? undefined) ||
      nextReportsTo !== position.parent_id;

    const resourceFieldsChanged =
      resourceSet != null &&
      (owningOrganization !== (resourceSet.owning_organization ?? '') ||
        capabilities !== (resourceSet.description ?? '') ||
        quantity !==
          (resourceSet.quantity != null ? String(resourceSet.quantity) : '') ||
        unit !== (resourceSet.unit ?? '') ||
        notes !== (resourceSet.notes ?? ''));

    if (rosterFieldsChanged) {
      updateRosterResource({
        positionId: position.id,
        pointOfContactUserId: nextPointOfContact,
        reportsToPositionId: nextReportsTo,
      });
    }

    if (resourceFieldsChanged && position.resourceSetId) {
      updateResourceSet({
        ...mapResourceSetToEditPayload(position.resourceSetId, resourceSet),
        owningOrganization,
        description: capabilities,
        quantity: quantity !== '' ? Number(quantity) : undefined,
        unit,
        notes,
      });
    }

    if (rosterFieldsChanged && !resourceFieldsChanged) {
      // useUpdateResourceSet raises its own toast; this covers the roster-only case.
      toast.success('Resource updated');
    }

    onClose();
  };

  return (
    <>
      <FormModal
        open={show}
        onOpenChange={(open) => {
          if (!open) onClose();
        }}
        title={position.name}
        footer={
          <div className="tw:flex tw:w-full tw:items-center tw:justify-between tw:gap-2">
            <Button
              type="button"
              variant="destructive-outline"
              onClick={() => setShowRemoveConfirm(true)}
            >
              Remove from Roster
            </Button>
            <div className="tw:flex tw:gap-2">
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button type="button" onClick={onSave}>
                Save Changes
              </Button>
            </div>
          </div>
        }
      >
        <div className="tw:space-y-4">
          <div className="tw:grid tw:gap-3 tw:md:grid-cols-3">
            <div>
              <div className="tw:font-semibold">Resource</div>
              <div>{resourceSet?.name ?? position.name}</div>
            </div>
            <div>
              <div className="tw:font-semibold">Kind</div>
              <div>
                {position.resourceType ?? resourceSet?.kind ?? NULL_VALUE}
              </div>
            </div>
            <div>
              <div className="tw:font-semibold">Type</div>
              <div>{resourceSet?.type ?? NULL_VALUE}</div>
            </div>
          </div>

          <div className="tw:grid tw:gap-4 tw:md:grid-cols-2">
            <SearchableSelect
              id="roster-resource-point-of-contact"
              label="Point of Contact"
              options={pointOfContactOptions}
              value={pointOfContact}
              onChange={setPointOfContact}
              placeholder="Select a point of contact"
            />
            <SearchableSelect
              id="roster-resource-reports-to"
              label="Reports To"
              options={reportsToOptions}
              value={reportsTo}
              onChange={setReportsTo}
              placeholder="Select a position"
              clearable
            />
            <div className="tw:space-y-2">
              <Label htmlFor="roster-resource-organization">
                Owning Organization
              </Label>
              <Input
                id="roster-resource-organization"
                value={owningOrganization}
                onChange={(event) => setOwningOrganization(event.target.value)}
              />
            </div>
            <div className="tw:space-y-2">
              <Label htmlFor="roster-resource-capabilities">Capabilities</Label>
              <Input
                id="roster-resource-capabilities"
                value={capabilities}
                onChange={(event) => setCapabilities(event.target.value)}
              />
            </div>
            <div className="tw:space-y-2">
              <Label htmlFor="roster-resource-quantity">Quantity</Label>
              <Input
                id="roster-resource-quantity"
                type="number"
                value={quantity}
                onChange={(event) => setQuantity(event.target.value)}
              />
            </div>
            <div className="tw:space-y-2">
              <Label htmlFor="roster-resource-unit">Unit</Label>
              <Input
                id="roster-resource-unit"
                value={unit}
                onChange={(event) => setUnit(event.target.value)}
              />
            </div>
            <div className="tw:space-y-2 tw:md:col-span-2">
              <Label htmlFor="roster-resource-notes">Notes</Label>
              <Textarea
                id="roster-resource-notes"
                rows={3}
                value={notes}
                onChange={(event) => setNotes(event.target.value)}
              />
            </div>
          </div>
        </div>
      </FormModal>

      <ConfirmDeleteDialog
        open={showRemoveConfirm}
        title="Remove Resource Confirmation"
        description={`Are you sure you want to remove ${position.name} from this roster? The resource itself is kept on the incident.`}
        confirmLabel="Remove from Roster"
        pending={isDetaching}
        onOpenChange={(open) => {
          if (!open) setShowRemoveConfirm(false);
        }}
        onConfirm={() => {
          detachResource(
            { positionId: position.id },
            {
              onSuccess: () => {
                toast.success('Resource removed from roster');
                setShowRemoveConfirm(false);
                onClose();
              },
            }
          );
        }}
      />
    </>
  );
};

export default RosterResourceModal;
